import React, { useEffect, useMemo, useRef, useState } from 'react';

type DeviceInfo = { deviceId: string; label: string };
type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selection: {
    micId: string | undefined;
    camId: string | undefined;
    speakerId: string | undefined;
  }) => void;
};

export default function TestAVModal({ isOpen, onClose, onConfirm }: Props) {
  const [mics, setMics] = useState<DeviceInfo[]>([]);
  const [cams, setCams] = useState<DeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<DeviceInfo[]>([]);

  const [micId, setMicId] = useState<string>();
  const [camId, setCamId] = useState<string>();
  const [speakerId, setSpeakerId] = useState<string>();

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioTestRef = useRef<HTMLAudioElement>(null);

  const micStreamRef = useRef<MediaStream | null>(null);
  const camStreamRef = useRef<MediaStream | null>(null);

  // audio meter
  const [micLevel, setMicLevel] = useState(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);

  // Real working 440Hz beep (0.5 seconds, mono WAV)
  const TEST_TONE_DATA_URI =
    'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

  useEffect(() => {
    if (!isOpen) return;
    // Prime permissions once so labels are available
    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      } catch {
        // If denied, we still try to enumerate; labels may be blank
      }
      await refreshDevices();
    })();

    const handleDeviceChange = () => refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      stopAll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Start/refresh camera preview when camId changes
    (async () => {
      await startCamera(camId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    // Start/refresh mic capture + meter when micId changes
    (async () => {
      await startMic(micId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [micId, isOpen]);

  async function refreshDevices() {
    const list = await navigator.mediaDevices.enumerateDevices();
    const micList = list
      .filter((d) => d.kind === 'audioinput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label || 'Microphone' }));
    const camList = list
      .filter((d) => d.kind === 'videoinput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label || 'Camera' }));
    const spkList = list
      .filter((d) => d.kind === 'audiooutput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label || 'Speakers' }));

    setMics(micList);
    setCams(camList);
    setSpeakers(spkList);

    if (!micId && micList[0]) setMicId(micList[0].deviceId);
    if (!camId && camList[0]) setCamId(camList[0].deviceId);
    if (!speakerId && spkList[0]) setSpeakerId(spkList[0].deviceId);
  }

  async function startCamera(deviceId?: string) {
    stopStream(camStreamRef);
    if (!deviceId) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false,
      });
      camStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      console.warn('Camera start failed:', e);
    }
  }

  async function startMic(deviceId?: string) {
    stopStream(micStreamRef);
    stopMeter();
    if (!deviceId) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      micStreamRef.current = stream;
      startMeter(stream);
    } catch (e) {
      console.warn('Mic start failed:', e);
    }
  }

  function startMeter(stream: MediaStream) {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;

      const pcm = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteTimeDomainData(pcm);
        // Compute RMS-like level 0..1
        let sum = 0;
        for (let i = 0; i < pcm.length; i++) {
          const v = (pcm[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / pcm.length);
        setMicLevel(Math.min(1, rms * 3));
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch (e) {
      console.warn('Meter failed:', e);
    }
  }

  function stopMeter() {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    analyserRef.current?.disconnect();
    analyserRef.current = null;
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
    setMicLevel(0);
  }

  function stopStream(ref: React.MutableRefObject<MediaStream | null>) {
    ref.current?.getTracks().forEach((t) => t.stop());
    ref.current = null;
  }

  function stopAll() {
    stopStream(micStreamRef);
    stopStream(camStreamRef);
    stopMeter();
  }

  async function playTestTone() {
    try {
      const el = audioTestRef.current;
      if (!el) return;

      // Reload in case src changed or element was suspended
      el.pause();
      el.currentTime = 0;
      el.load();

      // Speaker selection (Chromium only, HTTPS; not Safari/iOS)
      const canSetSink =
        typeof (el as any).setSinkId === 'function' &&
        window.isSecureContext;

      if (speakerId && canSetSink) {
        await (el as any).setSinkId(speakerId);
      }

      await el.play();
    } catch (e) {
      console.warn('Speaker test failed:', e);
    }
  }

  function handleClose() {
    stopAll();
    onClose();
  }

  function handleConfirm() {
    stopAll();
    onConfirm({ micId, camId, speakerId });
  }

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2731]/70 p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-3xl rounded-2xl border border-[#2C2731] bg-[#FEF6D6] p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-xl font-semibold text-[#2C2731]">Test your audio & video</h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Camera */}
          <div>
            <label className="mb-1 block text-sm font-medium text-[#2C2731]">Camera</label>
            <select
              className="w-full rounded-lg border border-[#2C2731] bg-[#FEF6D6] p-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
              value={camId || ''}
              onChange={(e) => setCamId(e.target.value)}
            >
              {cams.map((c) => (
                <option key={c.deviceId} value={c.deviceId}>
                  {c.label}
                </option>
              ))}
            </select>
            <div className="mt-3 aspect-video overflow-hidden rounded-xl bg-[#2C2731]">
              <video ref={videoRef} muted playsInline className="h-full w-full object-cover" />
            </div>
          </div>

          {/* Microphone */}
          <div>
            <label className="mb-1 block text-sm font-medium text-[#2C2731]">Microphone</label>
            <select
              className="w-full rounded-lg border border-[#2C2731] bg-[#FEF6D6] p-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
              value={micId || ''}
              onChange={(e) => setMicId(e.target.value)}
            >
              {mics.map((m) => (
                <option key={m.deviceId} value={m.deviceId}>
                  {m.label}
                </option>
              ))}
            </select>

            <div className="mt-3">
              <span className="text-sm font-medium text-[#2C2731]">Input level</span>
              <div className="mt-2 h-3 w-full rounded-full bg-[#E8DFBD]">
                <div
                  className="h-3 rounded-full bg-green-500 transition-[width]"
                  style={{ width: `${Math.floor(micLevel * 100)}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-[#2C2731]/70">Speak and watch the meter move.</p>
            </div>

            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-[#2C2731]">Speakers / Output</label>
              <select
                className="w-full rounded-lg border border-[#2C2731] bg-[#FEF6D6] p-2 text-sm text-[#2C2731] focus:border-[#2C2731] focus:outline-none focus:ring-2 focus:ring-[#2C2731]/30"
                value={speakerId || ''}
                onChange={(e) => setSpeakerId(e.target.value)}
              >
                {speakers.length ? (
                  speakers.map((s) => (
                    <option key={s.deviceId} value={s.deviceId}>
                      {s.label}
                    </option>
                  ))
                ) : (
                  <option value="">System default (browser)</option>
                )}
              </select>
              <button
                type="button"
                onClick={playTestTone}
                className="mt-3 w-full rounded-lg border border-[#2C2731] bg-[#FEF6D6] px-3 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FEF6D6]"
              >
                Play test sound
              </button>
              <audio ref={audioTestRef} src={TEST_TONE_DATA_URI} preload="auto" />
              <p className="mt-2 text-xs text-[#2C2731]/70">
                {speakers.length > 0
                  ? "If you don't hear the beep, pick another output or check OS volume."
                  : 'Speaker selection is not supported in Safari. Use system sound settings.'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-[#2C2731] bg-[#FEF6D6] px-4 py-2 text-sm font-medium text-[#2C2731] transition-colors hover:bg-[#E8DFBD] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2C2731] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FEF6D6]"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-medium text-[#FEF6D6] transition-colors hover:bg-[#1F1924] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FEF6D6] focus-visible:ring-offset-2 focus-visible:ring-offset-[#2C2731]"
          >
            Looks good
          </button>
        </div>

        <p className="mt-3 text-xs text-[#2C2731]/70">
          Tip: browsers sometimes hide device labels until you grant camera/mic permission.
        </p>
      </div>
    </div>
  );
}
